import { useState, useMemo, useEffect } from 'react';
import { Loader2, Mail, Pencil, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useGuestEngagement,
  useUpdateGuestEngagement,
  useSendTestGuestEngagement,
  useOnlineCheckout,
  useUpdateOnlineCheckout,
} from '@/hooks/api/useAdmin';
import type {
  GuestEngagementTemplate,
  GuestEngagementType,
} from '@/services/api/admin.api';
import type { OnlineCheckoutSettings } from '@pms/shared';

// ─── Constants ─────────────────────────────────────────────────────────────────

const TEMPLATE_TYPES: GuestEngagementType[] = [
  'confirmation',
  'cancellation',
  'arrival',
  'departure',
  'checkout',
];

const TEMPLATE_LABELS: Record<GuestEngagementType, string> = {
  confirmation: 'Confirmation',
  cancellation: 'Cancellation',
  arrival: 'Arrival-Day',
  departure: 'Departure / Feedback',
  checkout: 'Online Check-Out',
};

const MERGE_FIELDS = [
  '{{guestName}}',
  '{{checkInDate}}',
  '{{checkOutDate}}',
  '{{propertyName}}',
  '{{unitName}}',
  '{{bookingReference}}',
  '{{total}}',
  '{{currency}}',
];

// Sensible starting-point letters, pre-filled into the editor when a letter has
// no saved template yet. The hotelier can edit anything before saving. Tokens
// like {{guestName}} are swapped for each guest's real details when the letter
// is sent (see MERGE_FIELDS above).
const DEFAULT_TEMPLATES: Record<
  GuestEngagementType,
  { subject: string; body: string }
> = {
  confirmation: {
    subject: 'Your booking at {{propertyName}} is confirmed',
    body: `Dear {{guestName}},

Thank you for booking with {{propertyName}} — we're delighted to confirm your reservation.

Booking reference: {{bookingReference}}
Check-in: {{checkInDate}}
Check-out: {{checkOutDate}}
Room: {{unitName}}
Total: {{total}} {{currency}}

We look forward to welcoming you. If there's anything we can arrange before your stay, simply reply to this email.

Warm regards,
The {{propertyName}} Team`,
  },
  cancellation: {
    subject: 'Your booking at {{propertyName}} has been cancelled',
    body: `Dear {{guestName}},

This confirms that your reservation ({{bookingReference}}) at {{propertyName}} has been cancelled.

Original check-in: {{checkInDate}}
Original check-out: {{checkOutDate}}

We're sorry we won't be hosting you this time, and we hope to welcome you in the future. If this cancellation was made in error, please reply to this email right away.

Warm regards,
The {{propertyName}} Team`,
  },
  arrival: {
    subject: 'We look forward to welcoming you today, {{guestName}}',
    body: `Dear {{guestName}},

We're excited to welcome you to {{propertyName}} today. Here are your arrival details:

Booking reference: {{bookingReference}}
Room: {{unitName}}
Check-in: {{checkInDate}}

Our team is happy to help with an early check-in, luggage storage, or directions — just reply to this email or call us on arrival.

Safe travels, and see you soon.

Warm regards,
The {{propertyName}} Team`,
  },
  departure: {
    subject: 'Thank you for staying with us, {{guestName}}',
    body: `Dear {{guestName}},

Thank you for choosing {{propertyName}} for your stay — it was a pleasure to host you, and we hope you enjoyed your time with us.

We'd love to hear how it went. Your feedback helps us serve you even better next time, so simply reply to this email and let us know.

We hope to welcome you back to {{propertyName}} soon.

Warm regards,
The {{propertyName}} Team`,
  },
  checkout: {
    subject: 'Check out online from {{propertyName}}',
    body: `Dear {{guestName}},

Your departure from {{propertyName}} is coming up on {{checkOutDate}}, and you're welcome to check out online — no need to stop by the front desk.

Booking reference: {{bookingReference}}
Room: {{unitName}}

When you're ready, follow the online check-out link to settle any balance and confirm your departure. We hope you had a wonderful stay.

Warm regards,
The {{propertyName}} Team`,
  },
};

// ─── Page ────────────────────────────────────────────────────────────────────

export function GuestEngagementPage() {
  const propertyId = useSelectedPropertyId();
  const [editingType, setEditingType] = useState<GuestEngagementType | null>(
    null,
  );

  const templatesQuery = useGuestEngagement(propertyId || null);

  const byType = useMemo(() => {
    const map = new Map<GuestEngagementType, GuestEngagementTemplate>();
    for (const t of templatesQuery.data ?? []) {
      map.set(t.templateType, t);
    }
    return map;
  }, [templatesQuery.data]);

  if (!propertyId) {
    return (
      <div>
        <PageHeader
          title="Guest Engagement"
          description="Lifecycle letters and online check-out"
        />
        <EmptyState
          icon={Mail}
          title="Select a property"
          description="Select a property to continue."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Guest Engagement"
        description="Lifecycle letters and online check-out"
      />

      {/* Section A — Letters */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
          Letters
        </h2>

        {templatesQuery.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2
              size={32}
              className="animate-spin text-primary-500"
              aria-label="Loading"
            />
          </div>
        ) : templatesQuery.isError ? (
          <p
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            role="alert"
          >
            {extractErrorMessage(templatesQuery.error)}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TEMPLATE_TYPES.map((type) => {
              const tpl = byType.get(type);
              return (
                <div
                  key={type}
                  className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col"
                >
                  <div className="flex items-start justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {TEMPLATE_LABELS[type]}
                    </h3>
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                        tpl?.isEnabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500',
                      )}
                    >
                      {tpl?.isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2 min-h-[2.5rem]">
                    {tpl?.subject || (
                      <span className="italic text-gray-400">
                        Not configured
                      </span>
                    )}
                  </p>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setEditingType(type)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      <Pencil size={14} />
                      {tpl ? 'Edit' : 'Set up'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Section B — Online Check-Out */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
          Online Check-Out
        </h2>
        <OnlineCheckoutSection propertyId={propertyId} />
      </section>

      <SlideOver
        isOpen={editingType !== null}
        onClose={() => setEditingType(null)}
        title={
          editingType
            ? `Edit ${TEMPLATE_LABELS[editingType]} Letter`
            : 'Edit Letter'
        }
        width="max-w-xl"
      >
        {editingType && (
          <LetterForm
            propertyId={propertyId}
            type={editingType}
            template={byType.get(editingType) ?? null}
            onClose={() => setEditingType(null)}
          />
        )}
      </SlideOver>
    </div>
  );
}

// ─── Letter form ───────────────────────────────────────────────────────────────

function LetterForm({
  propertyId,
  type,
  template,
  onClose,
}: {
  propertyId: string;
  type: GuestEngagementType;
  template: GuestEngagementTemplate | null;
  onClose: () => void;
}) {
  // For a letter with no saved template yet, pre-fill the editor with a sensible
  // default so "Set up" opens ready-to-edit instead of blank. An existing
  // template always keeps its own saved subject/body.
  const defaults = DEFAULT_TEMPLATES[type];
  const [isEnabled, setIsEnabled] = useState(template?.isEnabled ?? false);
  const [subject, setSubject] = useState(template?.subject ?? defaults.subject);
  const [body, setBody] = useState(template?.body ?? defaults.body);
  const [testTo, setTestTo] = useState('');

  const updateMutation = useUpdateGuestEngagement();
  const testMutation = useSendTestGuestEngagement();

  // When enabled, subject + body are required (UX Flow 7 §3).
  const isValid =
    !isEnabled || (subject.trim().length > 0 && body.trim().length > 0);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    updateMutation.mutate(
      {
        propertyId,
        type,
        payload: {
          isEnabled,
          subject: subject.trim(),
          body,
        },
      },
      { onSuccess: () => onClose() },
    );
  }

  function handleSendTest() {
    if (!testTo.trim()) return;
    testMutation.mutate({ propertyId, type, to: testTo.trim() });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <input
            id="letter-enabled"
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label
            htmlFor="letter-enabled"
            className="text-sm font-medium text-gray-700"
          >
            Enabled
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          When on, this letter is sent automatically to the guest at this stage
          of their stay. Leave it off to keep the letter as a draft.
        </p>
      </div>

      <div>
        <label
          htmlFor="letter-subject"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Subject{isEnabled && <span className="text-red-500"> *</span>}
        </label>
        <p className="mb-1 text-xs text-gray-500">
          The email subject line the guest sees in their inbox.
        </p>
        <input
          id="letter-subject"
          type="text"
          maxLength={500}
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            updateMutation.reset();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {isEnabled && subject.trim().length === 0 && (
          <p className="mt-1 text-xs text-red-600">Subject is required when enabled.</p>
        )}
      </div>

      <div>
        <label
          htmlFor="letter-body"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Body{isEnabled && <span className="text-red-500"> *</span>}
        </label>
        <p className="mb-1 text-xs text-gray-500">
          The message itself. Tap a field below to drop in a placeholder like{' '}
          <code className="font-mono">{'{{guestName}}'}</code> — each one is
          replaced with the guest's real details (their name, dates, room, and
          so on) when the letter is sent.
        </p>
        <textarea
          id="letter-body"
          rows={10}
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            updateMutation.reset();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {isEnabled && body.trim().length === 0 && (
          <p className="mt-1 text-xs text-red-600">Body is required when enabled.</p>
        )}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {MERGE_FIELDS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setBody((b) => `${b}${f}`)}
              className="rounded bg-gray-100 px-2 py-0.5 text-xs font-mono text-gray-600 hover:bg-gray-200"
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Send test */}
      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <label
          htmlFor="letter-test-to"
          className="block text-xs font-medium text-gray-600"
        >
          Send a test email
        </label>
        <p className="text-xs text-gray-500">
          Sends a sample of this letter to the address below so you can see
          exactly how it looks before real guests receive it.
        </p>
        <div className="flex gap-2">
          <input
            id="letter-test-to"
            type="email"
            placeholder="you@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={handleSendTest}
            isLoading={testMutation.isPending}
            disabled={!testTo.trim() || !template}
          >
            <Send size={14} />
            Send test
          </Button>
        </div>
        {!template && (
          <p className="text-xs text-gray-400">
            Save the letter before sending a test.
          </p>
        )}
        {testMutation.isSuccess && (
          <p className="text-xs text-green-600" role="status">
            Test email sent.
          </p>
        )}
        {testMutation.isError && (
          <p className="text-xs text-red-600" role="alert">
            {extractErrorMessage(testMutation.error)}
          </p>
        )}
      </div>

      {updateMutation.isError && (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(updateMutation.error)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={updateMutation.isPending}
          disabled={!isValid}
        >
          Save
        </Button>
      </div>
    </form>
  );
}

// ─── Online Check-Out section ──────────────────────────────────────────────────

function OnlineCheckoutSection({ propertyId }: { propertyId: string }) {
  const settingsQuery = useOnlineCheckout(propertyId);
  const updateMutation = useUpdateOnlineCheckout();

  const settings = settingsQuery.data as OnlineCheckoutSettings | null;

  const [isEnabled, setIsEnabled] = useState(false);
  const [windowBefore, setWindowBefore] = useState('');
  const [windowAfter, setWindowAfter] = useState('');
  const [requireFolioSettled, setRequireFolioSettled] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  // Sync local form state once the settings load / change.
  useEffect(() => {
    setIsEnabled(settings?.isEnabled ?? false);
    setWindowBefore(
      settings?.windowHoursBefore != null
        ? String(settings.windowHoursBefore)
        : '',
    );
    setWindowAfter(
      settings?.windowHoursAfter != null
        ? String(settings.windowHoursAfter)
        : '',
    );
    setRequireFolioSettled(settings?.requireFolioSettled ?? false);
    setConfirmationMessage(settings?.confirmationMessage ?? '');
  }, [settings]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateMutation.mutate({
      propertyId,
      payload: {
        isEnabled,
        windowHoursBefore:
          windowBefore.trim() === '' ? undefined : Number(windowBefore),
        windowHoursAfter:
          windowAfter.trim() === '' ? undefined : Number(windowAfter),
        requireFolioSettled,
        confirmationMessage: confirmationMessage.trim() || undefined,
      },
    });
  }

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2
          size={28}
          className="animate-spin text-primary-500"
          aria-label="Loading"
        />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-xl border border-gray-200 p-4 space-y-4 max-w-xl"
    >
      <div>
        <div className="flex items-center gap-2">
          <input
            id="oc-enabled"
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label
            htmlFor="oc-enabled"
            className="text-sm font-medium text-gray-700"
          >
            Enable online check-out
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Lets guests check themselves out from their phone instead of visiting
          the front desk.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="oc-before"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Window — hours before departure
          </label>
          <p className="mb-1 text-xs text-gray-500">
            How early online check-out opens. E.g. 6 lets guests check out from
            6 hours before their checkout time.
          </p>
          <input
            id="oc-before"
            type="number"
            min={0}
            value={windowBefore}
            onChange={(e) => setWindowBefore(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label
            htmlFor="oc-after"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Window — grace hours after departure
          </label>
          <p className="mb-1 text-xs text-gray-500">
            How long online check-out stays open past checkout time, for guests
            running a little late. E.g. 2 keeps it open for 2 extra hours.
          </p>
          <input
            id="oc-after"
            type="number"
            min={0}
            value={windowAfter}
            onChange={(e) => setWindowAfter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2">
          <input
            id="oc-folio"
            type="checkbox"
            checked={requireFolioSettled}
            onChange={(e) => setRequireFolioSettled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label
            htmlFor="oc-folio"
            className="text-sm font-medium text-gray-700"
          >
            Require folio settled before check-out
          </label>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          When on, a guest can only check out online once their bill is fully
          paid (a zero balance). Anyone with an outstanding amount is asked to
          settle at the front desk.
        </p>
      </div>

      <div>
        <label
          htmlFor="oc-message"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Confirmation message
        </label>
        <p className="mb-1 text-xs text-gray-500">
          The thank-you note the guest sees on screen right after they finish
          checking out online.
        </p>
        <textarea
          id="oc-message"
          rows={3}
          value={confirmationMessage}
          onChange={(e) => setConfirmationMessage(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {updateMutation.isError && (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(updateMutation.error)}
        </p>
      )}
      {updateMutation.isSuccess && (
        <p className="text-sm text-green-600" role="status">
          Online check-out settings saved.
        </p>
      )}

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <Button type="submit" isLoading={updateMutation.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}
